export interface AutocompleteOption {
  value: string;
  label: string;
  type: 'vessel' | 'service' | 'subservice' | 'function' | 'tariff' | 'column' | 'createdSubservice' | 'tariff_column';
}

export class TypeaheadService {

  buildAutocompleteOptions(
    vesselProperties: string[],
    services: any[],
    tariffServices: any[],
    columns: string[]
  ): AutocompleteOption[] {
    const options: AutocompleteOption[] = [];

    // Vessel Properties
    vesselProperties.forEach(prop => {
      options.push({
        value: prop,
        label: prop,
        type: 'vessel'
      });
    });

    // Services (only service names)
    services.forEach(service => {
      options.push({
        value: service.name,
        label: service.name,
        type: 'service'
      });
    });

    // Sub-Services (service name -> sub service)
    services.forEach(service => {
      service.sub_services?.forEach((sub: any) => {
        options.push({
          value: `${service.name} -> ${sub.name}`,
          label: `${service.name} -> ${sub.name}`,
          type: 'subservice'
        });
      });
    });

    // Tariff Services (only tariff service names)
    tariffServices.forEach(tariff => {
      options.push({
        value: tariff.name,
        label: tariff.name,
        type: 'tariff'
      });
    });

    // Columns (only column names)
    columns.forEach(column => {
      options.push({
        value: column,
        label: column,
        type: 'column'
      });
    });

    return options;
  }

  buildConditionalAutocompleteOptions(
    vesselProperties: string[],
    createdServices: any[],
    createdSubServices: any[],
    selectedServiceName: string,
    existingSubServiceNames: string[]
  ): AutocompleteOption[] {
    const options: AutocompleteOption[] = [];

    // Vessel Properties (always available)
    vesselProperties.forEach(prop => {
      options.push({
        value: prop,
        label: prop,
        type: 'vessel'
      });
    });

    // Services (only created services in this port)
    createdServices.forEach(service => {
      options.push({
        value: service.service,
        label: service.service,
        type: 'service'
      });
    });

    // Sub-Services (only created sub-services with names)
    createdSubServices.forEach(subService => {
      options.push({
        value: subService.fullName,
        label: subService.fullName,
        type: 'subservice'
      });
    });
    // Created Sub-Service Names
    existingSubServiceNames.forEach(name => (
      options.push(
        {
          value: name,
          label: name,
          type: 'createdSubservice'
        }
      )
    ));

    // Sub-Services under Selected Service
    existingSubServiceNames?.forEach((sub: any) => {
      options.push({
        value: `${selectedServiceName} -> ${sub}`,
        label: `${selectedServiceName} -> ${sub}`,
        type: 'subservice'
      });
    });

    return options;
  }

  filterOptions(options: AutocompleteOption[], query: string): AutocompleteOption[] {
    if (!query) return options;

    return options.filter(option =>
      option.label.toLowerCase().includes(query.toLowerCase())
    );
  }

  getWordLevelSuggestions(input: string, options: AutocompleteOption[]): AutocompleteOption[] {
    if (!input) return options;

    // Get current word at cursor position
    const words = input.split(/\s+/);
    const currentWord = words[words.length - 1] || '';

    // If current word is empty (after space), show all options
    if (!currentWord.trim()) {
      return options;
    }

    // Filter based on current word
    return options.filter(option => {
      const optionWords = option.label.toLowerCase().split(/\s+/);
      return optionWords.some(word =>
        word.startsWith(currentWord.toLowerCase())
      ) || option.label.toLowerCase().includes(currentWord.toLowerCase());
    });
  }

  getContextualSuggestions(input: string, options: AutocompleteOption[]): AutocompleteOption[] {
    const trimmedInput = input.trim();

    // If input ends with space, show all options for next word
    if (input.endsWith(' ')) {
      return options;
    }

    // Get suggestions based on current context
    return this.getWordLevelSuggestions(trimmedInput, options);
  }

  getCurrentWordAtCursor(text: string, cursorPosition: number): { word: string, startPos: number, endPos: number } {
    const separators = /[,()\[\]{}+\*=<!|]/;

    // Special case: if cursor is right after any separator, append instead of replace
    if (cursorPosition > 0 && separators.test(text[cursorPosition - 1])) {
      return {
        word: '',
        startPos: cursorPosition,
        endPos: cursorPosition
      };
    }

    let startPos = cursorPosition;
    let endPos = cursorPosition;

    // Find start of current word
    while (startPos > 0 && !separators.test(text[startPos - 1])) {
      startPos--;
    }

    // Find end of current word
    while (endPos < text.length && !separators.test(text[endPos])) {
      endPos++;
    }

    return {
      word: text.substring(startPos, endPos),
      startPos,
      endPos
    };
  }

  filterOptionsByCursor(options: AutocompleteOption[], text: string, cursorPosition: number): AutocompleteOption[] {
    const { word } = this.getCurrentWordAtCursor(text, cursorPosition);

    if (!word.trim()) {
      return options;
    }

    return options.filter(option =>
      option.label.toLowerCase().startsWith(word.toLowerCase()) ||
      option.label.toLowerCase().includes(word.toLowerCase())
    );
  }

  insertTextAtCursor(originalText: string, cursorPosition: number, newText: string): { newValue: string, newCursorPos: number } {
    const { startPos, endPos } = this.getCurrentWordAtCursor(originalText, cursorPosition);

    const before = originalText.substring(0, startPos);
    const after = originalText.substring(endPos);

    const newValue = before + newText + after;
    const newCursorPos = startPos + newText.length;

    return { newValue, newCursorPos };
  }

}


